import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Link } from 'react-router-dom';
import CheckoutForm from './CheckoutForm';
import { supabase } from '../supabaseClient';

// Initialize Stripe
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51RZRdmR64Okod3pN7g91oOAZG57DxeiWTkW53Zzor2f0DPWBJvH4goAq1wOaKHXQ6ofbpfyCYrSLflvEE0tAoG3q00dvxpWJFB';
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

const styles = {
  container: {
    maxWidth: '800px',
    margin: '2rem auto',
    padding: '2rem',
    backgroundColor: 'var(--bg-color, #ffffff)',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '1rem',
    color: 'var(--text-color, #333)',
  },
  subtitle: {
    fontSize: '1.1rem',
    textAlign: 'center',
    marginBottom: '2rem',
    color: 'var(--text-light, #666)',
  },
  packagesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  packageCard: {
    border: '2px solid var(--border-color, #e1e5e9)',
    borderRadius: '12px',
    padding: '1.5rem',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backgroundColor: 'var(--bg-neutral, #f8f9fa)',
    position: 'relative',
  },
  packageCardSelected: {
    borderColor: 'var(--accent-color, #007bff)',
    backgroundColor: 'var(--accent-light, #f0f8ff)',
    transform: 'scale(1.02)',
  },
  packageCardPopular: {
    borderColor: 'var(--accent-secondary, #28a745)',
  },
  popularBadge: {
    position: 'absolute',
    top: '-10px',
    right: '20px',
    backgroundColor: 'var(--accent-secondary, #28a745)',
    color: 'white',
    padding: '0.25rem 0.75rem',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  packageName: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    color: 'var(--text-color, #333)',
  },
  packagePrice: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: 'var(--accent-color, #007bff)',
    marginBottom: '0.5rem',
  },
  packageDescription: {
    fontSize: '0.9rem',
    color: 'var(--text-light, #666)',
    marginBottom: '1rem',
  },
  packageUnlocks: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--text-color, #333)',
  },
  currentUnlocks: {
    textAlign: 'center',
    marginBottom: '2rem',
    padding: '1rem',
    backgroundColor: 'var(--bg-neutral-alt, #f3f4f6)',
    borderRadius: '8px',
    fontSize: '1.1rem',
  },
  checkoutSection: {
    marginTop: '2rem',
    padding: '2rem',
    backgroundColor: 'var(--bg-neutral, #f8f9fa)',
    borderRadius: '12px',
    border: '1px solid var(--border-color, #e1e5e9)',
  },
  checkoutTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    textAlign: 'center',
    color: 'var(--text-color, #333)',
  },
  loading: {
    textAlign: 'center',
    fontSize: '1.1rem',
    color: 'var(--text-light, #666)',
  },
  error: {
    color: 'var(--error-color, #dc3545)',
    textAlign: 'center',
    marginBottom: '1rem',
    padding: '0.75rem',
    backgroundColor: 'var(--error-bg, #f8d7da)',
    borderRadius: '6px',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: 'var(--bg-neutral, #f8f9fa)',
    color: 'var(--text-color, #333)',
    border: '2px solid var(--border-color, #e1e5e9)',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
    marginBottom: '1.5rem',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
  },
  backButtonHover: {
    backgroundColor: 'var(--bg-neutral-alt, #f3f4f6)',
    borderColor: 'var(--accent-color, #007bff)',
    transform: 'translateY(-1px)',
  }
};

export default function PurchaseUnlocks() {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [currentUnlocks, setCurrentUnlocks] = useState(0);
  const [currentCredits, setCurrentCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      setUser(currentUser);
      
      // If no user, we'll still load packages but won't load user-specific data
      if (!currentUser) {
        setCurrentUnlocks(0);
        setCurrentCredits(0);
      } else {
        console.log('DEBUG: PurchaseUnlocks - Fetching unlock info for user ID:', currentUser.id);

        // Always use direct Supabase query for reliable credit display
        console.log('DEBUG: PurchaseUnlocks - Fetching user data for user:', currentUser.id);
        
        // First try to get all user data to see what columns exist
        const { data: userData, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', currentUser.id)
          .single();
        
        console.log('DEBUG: PurchaseUnlocks - Raw user data:', userData);
        console.log('DEBUG: PurchaseUnlocks - Error:', error);
        
        if (!error && userData) {
          // Try different possible column names for credits
          const unlockCount = userData.unlock_count || 0;
          const creditsAmount = userData.credits || userData.credit_count || 0;
          
          console.log('DEBUG: unlock_count:', unlockCount);
          console.log('DEBUG: credits:', creditsAmount);
          
          setCurrentUnlocks(unlockCount);
          setCurrentCredits(creditsAmount);
          
          console.log('DEBUG: Setting total credits to:', unlockCount + creditsAmount);
        } else {
          console.error('DEBUG: PurchaseUnlocks - Error fetching user data:', error);
          setCurrentUnlocks(0);
          setCurrentCredits(0);
        }
      }

      // Get packages from Supabase Edge Function
      try {
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || supabase.supabaseUrl;
        const packagesResponse = await fetch(`${supabaseUrl}/functions/v1/get-unlock-packages`, {
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          }
        });

        if (packagesResponse.ok) {
          const packagesData = await packagesResponse.json();
          setPackages(packagesData);
        } else {
          throw new Error('Failed to fetch packages');
        }
      } catch (packagesErr) {
        console.error('Error fetching packages:', packagesErr);
        // Fallback packages if Edge Function fails
        setPackages([
          { id: 'basic', name: '5 Unlock Credits', unlocks: 5, credits: 10, price: 4.99, description: 'Perfect for exploring a few profiles' },
          { id: 'standard', name: '15 Unlock Credits', unlocks: 15, credits: 30, price: 12.99, description: 'Great value for regular users', popular: true },
          { id: 'premium', name: '50 Unlock Credits', unlocks: 50, credits: 100, price: 29.99, description: 'Best for power users' }
        ]);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePackageSelect = async (pkg) => {
    if (!user) {
      // Show login prompt instead of just returning
      setError('Please log in to purchase unlock credits. Create an account or sign in to access this feature.');
      return;
    }

    try {
      setSelectedPackage(pkg);
      setError('');

      // Call Supabase Edge Function to create payment intent
      try {
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || supabase.supabaseUrl;
        const response = await fetch(`${supabaseUrl}/functions/v1/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: pkg.price,
            unlockCount: pkg.unlocks,
            creditCount: pkg.credits || pkg.unlocks * 2,
            userId: user.id
          })
        });

        if (response.ok) {
          const { clientSecret } = await response.json();
          setClientSecret(clientSecret);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create payment intent');
        }
      } catch (edgeFunctionErr) {
        console.log('Edge Function error:', edgeFunctionErr);
        setError(`Payment system error: ${edgeFunctionErr.message}. Please make sure your Supabase Edge Functions are deployed and configured.`);
      }
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setError(err.message || 'Failed to initialize payment');
    }
  };

  const handlePaymentSuccess = () => {
    // Refresh the current unlocks count
    loadInitialData();
    setSelectedPackage(null);
    setClientSecret('');
  };

  const handleBackToPackages = () => {
    setSelectedPackage(null);
    setClientSecret('');
    setError('');
  };

  if (loading) {
    return <div style={styles.container}><div style={styles.loading}>Loading...</div></div>;
  }

  // Debug: Show which key is being used
  console.log('Stripe key from env:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  console.log('Using Stripe key:', stripePublishableKey);

  if (!stripePublishableKey) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Payment System Not Configured</h1>
        <div style={styles.error}>
          Stripe payment system is not configured. Please set up your Stripe API keys in the environment variables.
        </div>
      </div>
    );
  }

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#007bff',
    },
  };

  const options = {
    clientSecret,
    appearance,
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Purchase Unlocks</h1>
      <p style={styles.subtitle}>
        Get more unlocks to view additional profiles and essays
      </p>

      {user && (
        <div style={styles.currentUnlocks}>
          <strong>Current Credits: {currentUnlocks + currentCredits}</strong>
          <div style={{ fontSize: '0.9em', marginTop: '0.5rem', color: 'var(--text-light, #666)' }}>
            Credits for viewing profiles and essays
          </div>
          <button 
            onClick={loadInitialData}
            style={{
              marginTop: '0.5rem',
              padding: '0.25rem 0.75rem',
              fontSize: '0.8rem',
              backgroundColor: 'var(--accent-color, #007bff)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Refresh Credits
          </button>
        </div>
      )}



      {error && (
        <div style={styles.error}>
          {error}
          {error.includes('Please log in') && (
            <div style={{ marginTop: '1rem' }}>
              <Link 
                to="/login" 
                style={{
                  display: 'inline-block',
                  backgroundColor: 'var(--accent-color, #007bff)',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  transition: 'all 0.2s ease'
                }}
              >
                Login / Sign Up
              </Link>
            </div>
          )}
        </div>
      )}

      {!selectedPackage ? (
        <div style={styles.packagesGrid}>
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              style={{
                ...styles.packageCard,
                ...(pkg.popular ? styles.packageCardPopular : {})
              }}
              onClick={() => handlePackageSelect(pkg)}
            >
              {pkg.popular && <div style={styles.popularBadge}>Most Popular</div>}
              <h3 style={styles.packageName}>{pkg.name}</h3>
              <div style={styles.packagePrice}>${pkg.price}</div>
              <p style={styles.packageDescription}>{pkg.description}</p>
              <div style={{
                fontSize: '0.85rem',
                color: 'var(--accent-color, #007bff)',
                fontWeight: '600',
                marginTop: '0.5rem'
              }}>
                {pkg.unlocks === 5 ? 'Enough for ~1 profile (5 credits avg)' :
                 pkg.unlocks === 15 ? 'Enough for ~3 profiles (5 credits avg)' :
                 pkg.unlocks === 50 ? 'Enough for ~10 profiles (5 credits avg)' :
                 `Enough for ~${Math.floor(pkg.unlocks / 5)} profiles (5 credits avg)`}
              </div>

            </div>
          ))}
        </div>
      ) : (
        <div style={styles.checkoutSection}>
          <button 
            style={styles.backButton} 
            onClick={handleBackToPackages}
            onMouseEnter={(e) => {
              Object.assign(e.target.style, styles.backButtonHover);
            }}
            onMouseLeave={(e) => {
              Object.assign(e.target.style, styles.backButton);
            }}
          >
            <span>←</span>
            <span>Back to Packages</span>
          </button>
          <h2 style={styles.checkoutTitle}>
            Complete Your Purchase: {selectedPackage.name}
          </h2>
          
          {clientSecret && (
            <Elements options={options} stripe={stripePromise}>
              <CheckoutForm 
                onSuccess={handlePaymentSuccess}
                packageInfo={selectedPackage}
              />
            </Elements>
          )}
        </div>
      )}
    </div>
  );
} 